/**
 * Authoring a block's visibility condition.
 *
 * The model (`Condition`), the reducer action (`setVisibility`) and the emission
 * (`compileCondition` → an `{% if %}` wrapper) all shipped in Sprint 0.6 — and no UI ever
 * reached them, so a complete, tested, compiling feature was unusable. This module is the
 * missing half's pure part: what the operators are, when a condition is worth keeping,
 * and how to read one back as a sentence.
 *
 * ### Why blocks use `{% if %}` and text cannot
 *
 * A block is emitted as *markup*, so wrapping it in a statement is exactly right. A
 * **text** conditional cannot work this way: a text prop is passed as a *value*, and Twig
 * does not re-parse a string argument, so `{% if %}` inside one reaches the recipient as
 * those characters. Text conditionals have to be expressions — see the design note in
 * `docs/plans/email-editor-unified.md`.
 */
import { Condition, ConditionClause, ConditionOperator } from './document';

/**
 * The operators, in the order the UI offers them: presence first, then equality,
 * then containment, then the numeric comparisons — narrowing from "is there
 * anything at all" to "is it more than five".
 */
export const CONDITION_OPERATORS: ConditionOperator[] = [
  'set',
  'unset',
  'eq',
  'neq',
  'contains',
  'ncontains',
  'gt',
  'gte',
  'lt',
  'lte',
];

/** The two that stand alone; every other operator compares against a value. */
const VALUELESS_OPERATORS = new Set<ConditionOperator>(['set', 'unset']);

/** True for the operators that compare against a value the author types. */
export function operatorNeedsValue(op: ConditionOperator): boolean {
  return !VALUELESS_OPERATORS.has(op);
}

/** True for the operators whose typed value only makes sense as a number. */
export function operatorIsNumeric(op: ConditionOperator): boolean {
  return op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte';
}

/** A fresh clause: the common case is "this value exists". */
export function emptyClause(): ConditionClause {
  return { path: '', op: 'set' };
}

/** A condition with one empty clause — what "add a condition" starts from. */
export function emptyCondition(): Condition {
  return { mode: 'all', clauses: [emptyClause()] };
}

/**
 * Only the clauses that would compile.
 *
 * A clause with no variable is half-typed, not a rule. `compile()` already warns
 * (`EMPTY_CONDITION`) when a condition has no usable clauses and shows the block anyway —
 * the UI's job is to not create that state in the first place.
 */
export function usableClauses(condition: Condition): ConditionClause[] {
  return condition.clauses.filter((clause) => clause.path.trim() !== '');
}

/**
 * What to store for a condition being edited, or **null** to drop it entirely.
 *
 * Null when nothing usable is left, so deleting the last clause removes the condition
 * rather than leaving a block that is conditional on nothing — which reads as "hidden"
 * to anyone who opens it later and is not what the author meant.
 */
export function normalizeCondition(condition: Condition): Condition | null {
  const clauses = usableClauses(condition);
  if (clauses.length === 0) return null;
  return { mode: condition.mode, clauses };
}

export function updateClause(
  condition: Condition,
  index: number,
  patch: Partial<ConditionClause>,
): Condition {
  const clauses = condition.clauses.map((clause, at) => {
    if (at !== index) return clause;
    const next = { ...clause, ...patch };
    // Dropping the compared value with the operator keeps the stored clause honest —
    // a lingering `value` on a `set` clause would reappear if the operator changed back.
    if (!operatorNeedsValue(next.op)) delete next.value;
    return next;
  });
  return { ...condition, clauses };
}

export function addClause(condition: Condition): Condition {
  return { ...condition, clauses: [...condition.clauses, emptyClause()] };
}

export function removeClause(condition: Condition, index: number): Condition {
  return {
    ...condition,
    clauses: condition.clauses.filter((_, at) => at !== index),
  };
}

/** Every block id in a document that is shown conditionally, for the canvas marker. */
export function conditionalBlockIds(
  blocks: { id: string; visibility?: Condition; children?: unknown }[],
  into: Set<string> = new Set(),
): Set<string> {
  for (const block of blocks) {
    if (block.visibility && usableClauses(block.visibility).length > 0) {
      into.add(block.id);
    }
    const slots = (block.children ?? []) as {
      id: string;
      visibility?: Condition;
      children?: unknown;
    }[][];
    for (const slot of slots) conditionalBlockIds(slot, into);
  }
  return into;
}
